import { useState } from 'react';
import { supabase } from '../../../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff, FiCheckCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';

const ResetPasswordForm = ({ setAuthError, setAuthMessage }) => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    if (setAuthError) setAuthError('');
    if (setAuthMessage) setAuthMessage('');
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (formData.password.length < 6) {
      if (setAuthError) setAuthError('Password should be at least 6 characters.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      if (setAuthError) setAuthError('Passwords do not match.');
      return;
    }

    setLoading(true);
    if (setAuthError) setAuthError('');
    if (setAuthMessage) setAuthMessage('');

    try {
      const { error } = await supabase.auth.updateUser({
        password: formData.password,
      });

      if (error) throw error;

      setIsSuccess(true);
      if (setAuthMessage) setAuthMessage('Password reset successfully! Redirecting...');
      toast.success('Password reset successfully!');

      setTimeout(() => {
        navigate('/user-dashboard');
      }, 1500);
    } catch (err) {
      const msg = err.message || 'Failed to reset password. Please try again.';
      if (setAuthError) setAuthError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="auth-form" style={{ textAlign: 'center', padding: '1rem 0' }}>
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#ecfdf5',
            color: '#10b981',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            fontSize: '1.75rem',
          }}
        >
          <FiCheckCircle />
        </div>
        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1e293b', marginBottom: '0.5rem' }}>
          Password Updated!
        </h3>
        <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
          Your password has been changed successfully. Redirecting you to your dashboard...
        </p>
      </div>
    );
  }

  const passwordsMatch = formData.confirmPassword.length > 0 && formData.password === formData.confirmPassword;
  const passwordsMismatch = formData.confirmPassword.length > 0 && formData.password !== formData.confirmPassword;

  return (
    <form className="auth-form" onSubmit={handleResetPassword}>
      <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem', textAlign: 'center' }}>
        Please enter your new password below (minimum 6 characters).
      </p>

      <div className="form-group">
        <label htmlFor="password">New Password</label>
        <div className="password-input-container">
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            className="form-input"
            placeholder="At least 6 characters"
            required
            disabled={loading}
            autoComplete="new-password"
            minLength={6}
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword(!showPassword)}
            disabled={loading}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
          </button>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="confirmPassword">Confirm Password</label>
        <div className="password-input-container">
          <input
            id="confirmPassword"
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleChange}
            className="form-input"
            placeholder="Re-enter your new password"
            required
            disabled={loading}
            autoComplete="new-password"
            minLength={6}
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            disabled={loading}
            aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
          >
            {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
          </button>
        </div>
        {passwordsMismatch && (
          <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>
            Passwords do not match
          </span>
        )}
        {passwordsMatch && (
          <span style={{ color: '#10b981', fontSize: '0.75rem', marginTop: '0.25rem' }}>
            ✓ Passwords match
          </span>
        )}
      </div>

      <button
        type="submit"
        className="submit-button"
        disabled={loading || formData.password.length < 6 || formData.password !== formData.confirmPassword}
      >
        {loading ? (
          <>
            <span className="loading-spinner">↻</span> Updating Password...
          </>
        ) : (
          'Update Password'
        )}
      </button>
    </form>
  );
};

export default ResetPasswordForm;

